import { Button } from '@civilgrid/ui/components';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { updateLinkedOrganizations as apiUpdateLinkedOrganizations } from '~/api/internal';
import { getOrganization } from '~/api/organizations';
import type { LinkedOrganizationDetail, OrganizationDetail } from '~/common/types';
import { Notify } from '~/components2/Notify';
import OrganizationPicker from '~/components2/OrganizationPicker';

type TFormValues = {
  linkedOrganizationIds: string[];
};

interface ComponentState {
  targetOrg: OrganizationDetail | null;
  linkedOrgs: LinkedOrganizationDetail[];
  isLoading: boolean;
  isProcessing: boolean;
}

export default function UpdateLinkedOrganizations() {
  const [state, setState] = useState<ComponentState>({
    targetOrg: null,
    linkedOrgs: [],
    isLoading: false,
    isProcessing: false,
  });

  const form = useForm<TFormValues>({
    defaultValues: { linkedOrganizationIds: [] },
  });

  const handleTargetOrgSelect = async (organizationId: string, organization: OrganizationDetail) => {
    setState((prev) => ({ ...prev, isLoading: true, targetOrg: organization, linkedOrgs: [] }));
    try {
      const fullOrg = await getOrganization(organizationId);
      const linked = fullOrg.linkedOrganizations ?? [];
      form.reset({ linkedOrganizationIds: linked.map((o: Record<string, string>) => o.id) });
      setState((prev) => ({ ...prev, linkedOrgs: linked, isLoading: false }));
    } catch {
      Notify.error({ message: 'Error loading organization details' });
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const setLinkedOrgs = (next: LinkedOrganizationDetail[]) => {
    setState((prev) => ({ ...prev, linkedOrgs: next }));
    form.setValue(
      'linkedOrganizationIds',
      next.map((o) => o.id),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const handleAddOrg = (_: string, org: OrganizationDetail) => {
    if (org.id === state.targetOrg?.id) {
      Notify.error({ message: 'Cannot link an organization to itself' });
      return;
    }
    if (state.linkedOrgs.some((o) => o.id === org.id)) return;
    setLinkedOrgs([...state.linkedOrgs, { id: org.id, name: org.name }]);
  };

  const handleRemoveOrg = (orgId: string) => {
    setLinkedOrgs(state.linkedOrgs.filter((o) => o.id !== orgId));
  };

  const handleSave = async () => {
    if (!state.targetOrg) return;
    setState((prev) => ({ ...prev, isProcessing: true }));
    try {
      await apiUpdateLinkedOrganizations(
        state.targetOrg.id,
        state.linkedOrgs.map((o) => o.id),
      );
      form.reset({ linkedOrganizationIds: state.linkedOrgs.map((o) => o.id) });
      Notify.info({ message: `Linked organizations updated for ${state.targetOrg.name}` });
    } catch {
      Notify.error({ message: 'Error updating linked organizations' });
    } finally {
      setState((prev) => ({ ...prev, isProcessing: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="text-2xs font-medium uppercase tracking-wider text-gray-500">Parent Organization</div>
        <OrganizationPicker selectedOrganization={state.targetOrg} onOrganizationSelect={handleTargetOrgSelect} />
      </div>

      {state.targetOrg && (
        <>
          <div className="space-y-1">
            <div className="text-2xs font-medium uppercase tracking-wider text-gray-500">Linked Organizations</div>
            {state.isLoading ? (
              <div className="text-xs text-gray-400">Loading...</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {state.linkedOrgs.map((org) => (
                  <span
                    key={org.id}
                    className="inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                  >
                    {org.name}
                    <button
                      type="button"
                      onClick={() => handleRemoveOrg(org.id)}
                      className="ml-0.5 text-gray-400 hover:text-gray-700"
                      aria-label={`Remove ${org.name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {state.linkedOrgs.length === 0 && (
                  <span className="text-xs italic text-gray-400">No linked organizations.</span>
                )}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <div className="text-2xs font-medium uppercase tracking-wider text-gray-500">Add Linked Organization</div>
            <OrganizationPicker
              selectedOrganization={null}
              onOrganizationSelect={handleAddOrg}
              placeholder="Select org to link..."
            />
          </div>

          <Button
            onClick={handleSave}
            loading={state.isProcessing}
            disabled={state.isProcessing || state.isLoading || !form.formState.isDirty}
          >
            Save
          </Button>
        </>
      )}
    </div>
  );
}
